// Skills, user-authored skills, and the slash-command catalog client.
//
// Lifted out of `../api` so the file boundary matches the backend
// `src/railyard/routes/skills.py` + `routes/commands.py`. Three logical
// groups served by one router on the backend, mirrored here.

import type { components } from "@/lib/api-types";

import { prepareUserSkillEdit } from "@/lib/skill-display-title";

import {
  API,
  apiFetch,
  requestJson,
  requestOk,
} from "../api";


type Schemas = components["schemas"];


// ---------------------------------------------------------------------------
// Skills catalog (firm + vendor tiers)
// ---------------------------------------------------------------------------

export type Skill = Schemas["Skill"];

export async function listSkills(
  opts?: { includeDisabled?: boolean; signal?: AbortSignal },
): Promise<Skill[]> {
  const q = opts?.includeDisabled ? "?include_disabled=true" : "";
  return requestJson(`${API}/api/skills${q}`, { signal: opts?.signal });
}

/** Admin-only: enable or disable a skill for the workspace. A disabled skill
 *  drops out of the catalog (web + MCP) and can't be loaded by the agent. */
export async function setSkillEnabled(name: string, enabled: boolean): Promise<void> {
  await requestOk(`${API}/api/admin/skills/${encodeURIComponent(name)}/enabled`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
}

export async function uploadSkill(file: File): Promise<Skill> {
  const form = new FormData();
  form.append("file", file);
  return requestJson(`${API}/api/skills/upload`, {
    method: "POST",
    body: form,
  });
}

export async function deleteSkill(name: string): Promise<void> {
  await requestOk(
    `${API}/api/skills/${encodeURIComponent(name)}`,
    { method: "DELETE" },
    { allowStatus: [404] },
  );
}

export type SkillDetail = Schemas["SkillDetail"];

/** One resolved skill with its SKILL.md body — backs the skill detail page.
 *  Resolution mirrors the catalog (user-authored shadows registry by name);
 *  `editable` is true only for authored skills the caller may edit in place. */
export async function getSkillDetail(
  name: string,
  signal?: AbortSignal,
): Promise<SkillDetail> {
  return requestJson(`${API}/api/skills/${encodeURIComponent(name)}`, { signal });
}

/** Download a firm-tier skill's original `.skill` (zip) archive. Admin-only on
 *  the server; streams the blob and triggers a browser save. */
export async function downloadFirmSkillArchive(name: string): Promise<void> {
  const r = await apiFetch(
    `${API}/api/admin/skills/${encodeURIComponent(name)}/archive`,
  );
  if (!r.ok) throw new Error(`Download failed (${r.status})`);
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}.skill`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


// ---------------------------------------------------------------------------
// Deck-skill versions (Deck Studio) — immutable versions + a published pointer
// ---------------------------------------------------------------------------

export type SkillVersions = Schemas["SkillVersionsResponse"];

/** List a skill's published versions + the current published pointer. A skill
 *  with no published versions returns an empty `versions` and null `published`
 *  — the Deck Studio panel uses that to show only versioned (deck) skills. */
export async function getSkillVersions(
  skillId: string,
  signal?: AbortSignal,
): Promise<SkillVersions> {
  return requestJson(
    `${API}/api/skills/${encodeURIComponent(skillId)}/versions`,
    { signal },
  );
}

export type SkillSources = Schemas["SkillSourcesResponse"];

/** The example decks a deck-skill was authored from (its `## Sources`
 *  provenance). Drives the "Enhance" action — re-mounted as attachments so an
 *  enhance run re-opens the skill against the same decks. Empty for skills
 *  published before provenance (or with none recorded). */
export async function getSkillSources(
  skillId: string,
  signal?: AbortSignal,
): Promise<SkillSources> {
  return requestJson(
    `${API}/api/skills/${encodeURIComponent(skillId)}/sources`,
    { signal },
  );
}

/** Move the published pointer to an existing version (rollback / promote).
 *  Admin-only on the server. */
export async function setPublishedSkillVersion(
  skillId: string,
  version: string,
): Promise<SkillVersions> {
  return requestJson(
    `${API}/api/skills/${encodeURIComponent(skillId)}/published`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version }),
    },
  );
}


// ---------------------------------------------------------------------------
// Commands (slash commands — project → workspace → built-in catalog)
// ---------------------------------------------------------------------------

export type CommandSummary = Schemas["CommandSummary"];

export async function listCommands(
  opts: { projectId?: string | null; signal?: AbortSignal } = {},
): Promise<CommandSummary[]> {
  const qs = opts.projectId
    ? `?project_id=${encodeURIComponent(opts.projectId)}`
    : "";
  const resp = await requestJson<Schemas["CommandsListResponse"]>(
    `${API}/api/commands${qs}`,
    { signal: opts.signal },
  );
  return resp.commands;
}

/** Run a command as a card: server creates a command-tagged task, attaches
 *  the command's bound outcome (if any), and starts a detached run. Returns
 *  the new task id so the caller can navigate to the live task. */
export async function runCommand(
  name: string,
  opts: { projectId?: string | null; inputs?: string } = {},
): Promise<string> {
  const body: Record<string, unknown> = {};
  if (opts.projectId) body.project_id = opts.projectId;
  if (opts.inputs && opts.inputs.trim()) body.inputs = opts.inputs.trim();
  const resp = await requestJson<Schemas["CommandRunResponse"]>(
    `${API}/api/commands/${encodeURIComponent(name)}/run`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return resp.task_id;
}


// ---------------------------------------------------------------------------
// Graders — admin-curated, reusable outcome rubrics (definitions of done)
// ---------------------------------------------------------------------------

export type Grader = Schemas["GraderResponse"];

export async function listGraders(signal?: AbortSignal): Promise<Grader[]> {
  const resp = await requestJson<Schemas["GraderListResponse"]>(
    `${API}/api/graders`,
    { signal },
  );
  return resp.graders;
}

type GraderWrite = {
  name?: string;
  title: string;
  description?: string;
  body: string;
  max_iterations?: number | null;
};

export async function createGrader(body: GraderWrite): Promise<Grader> {
  return requestJson(`${API}/api/graders`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateGrader(
  name: string,
  body: GraderWrite,
): Promise<Grader> {
  return requestJson(`${API}/api/graders/${encodeURIComponent(name)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteGrader(name: string): Promise<void> {
  await requestOk(`${API}/api/graders/${encodeURIComponent(name)}`, {
    method: "DELETE",
  });
}


// ---------------------------------------------------------------------------
// User-authored skills (per-user; promotable to firm tier)
// ---------------------------------------------------------------------------

export type UserSkill = Schemas["UserSkill"];

export async function listUserSkills(): Promise<UserSkill[]> {
  return requestJson(`${API}/api/user-skills`);
}

export async function setUserSkillVisibility(
  name: string,
  visibility: "user" | "firm",
): Promise<UserSkill> {
  return requestJson(
    `${API}/api/user-skills/${encodeURIComponent(name)}/visibility`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visibility }),
    },
  );
}

/** Edit a user-authored skill's display title, description, and instructions.
 *  The `name` argument is the slug and is not rewritten from the title.
 *  Owner-gated for private skills, admin-gated for firm skills (server-side);
 *  the edit is versioned and re-delivered to sessions for firm skills.
 *
 *  An empty or invalid display title, or empty instructions, throws before
 *  the request. The server would otherwise store a blank title as the slug.
 *  `signal` aborts the client PUT when the editor gives up (Cancel, or the save
 *  deadline) — it cannot roll back a request the server already committed. */
export async function updateUserSkill(
  name: string,
  body: { title: string; description: string; body: string },
  signal?: AbortSignal,
): Promise<UserSkill> {
  const prepared = prepareUserSkillEdit({ name, ...body });
  if (!prepared.ok) {
    throw new Error(
      prepared.titleError ?? prepared.instructionsError ?? "Invalid skill edit.",
    );
  }
  return requestJson(`${API}/api/user-skills/${encodeURIComponent(prepared.name)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      title: prepared.title,
      description: prepared.description,
      body: prepared.body,
    }),
    signal,
  });
}

export async function deleteUserSkill(name: string): Promise<void> {
  await requestOk(
    `${API}/api/user-skills/${encodeURIComponent(name)}`,
    { method: "DELETE" },
    { allowStatus: [404] },
  );
}


// ---------------------------------------------------------------------------
// New-skill task starter — boots a task pre-loaded with the skill-creator
// trigger prompt. Lives next to the user-skill surface since that's the
// thing it creates.
// ---------------------------------------------------------------------------

export async function startNewSkillTask(): Promise<{ task_id: string }> {
  return requestJson(`${API}/api/skills/new-task`, { method: "POST" });
}
